import { Dispatch, MutableRefObject, SetStateAction, memo, useMemo } from "react"
import CategoryFilter from "../categoryFilter"
import { CustomHeaderWrapper } from "./styles"
import FullCalendar from "@fullcalendar/react"
import { Grid } from "@mui/material"
import { useTranslation } from "react-i18next"
import ManualReservation from "./actions/manualReservation"
import GoToday from "./actions/goToday"
import ControlsMonthlyView from "./ControlsMonthlyView"
import SwitchView from "./actions/switchView"
import SwitchProperties from "./actions/switchProperties"
import SwitchReservations from "./actions/switchReservations"
import WheelhouseSection from "./wheelhouseSection"
import { IResponseGetCalenderData } from "src/services/calender/getCalenderData/types"
import { useSubPermissions } from "@hooks/usePermissions"
import { useIsFreemium } from "@hooks/useIsFreemium"

interface IProps {
	calendarRef: MutableRefObject<FullCalendar | null>
	switchView: "dayGridMonth" | "resourceTimelineWeek"
	setSwitchView: Dispatch<
		SetStateAction<"dayGridMonth" | "resourceTimelineWeek">
	>
	calendarTitle: string
	getCalenderData: IResponseGetCalenderData | undefined
	selectedCategoryIds: number[]
	onCategoryChange: (ids: number[]) => void
	onCategoryPropertyIds: (ids: number[]) => void
	hideControls?: boolean
}
function CustomHeader({
	calendarRef,
	switchView,
	setSwitchView,
	calendarTitle,
	getCalenderData,
	selectedCategoryIds,
	onCategoryChange,
	onCategoryPropertyIds,
	hideControls,
}: IProps) {
	/* ---------------------------------- Hooks --------------------------------- */
	const { t } = useTranslation()
	const { showCalendarManualBooking, showReservationsManualbooking } = useSubPermissions()
	// Freemium has no OTAs: no category filter, no reservation/request switch.
	const isFreemium = useIsFreemium()
	// Freemium: the role seed currently links ManualBooking under the Reservations
	// tab (backend data issue — MS-407 intends it under Calendar too), which hides
	// this button while the reservations page shows it. Until the seed is fixed,
	// accept the grant from either tab — scoped to freemium so team-role
	// per-page permissions keep their exact semantics.
	const showManualBooking =
		showCalendarManualBooking || (isFreemium && showReservationsManualbooking)
	
	// Memoize the condition to prevent unnecessary re-renders of WheelhouseSection
	const isWheelhouseActive = useMemo(() => {
		return getCalenderData?.result?.some(item => item.isWheelhouseActive)
	}, [getCalenderData?.result])
	
	return (
		<CustomHeaderWrapper>
			<Grid container  justifyContent='flex-end' gap='24px'>
				<Grid item xs={12} className='firstGrid'>
					<Grid container justifyContent='space-between'>
						<Grid item xs={12} md={6}>
							<div className='containerLabel'>
								<p>{t("CALENDAR.CALENDAR")}</p>
								<p>
									{t("CALENDAR.A_SPACE_TO_HELP_YOU_REVIEW_YOUR_RESERVATIONS")}
								</p>
							</div>
						</Grid>
						{showManualBooking && (
							<Grid item xs={12} md={6}>
								<div className='containerManualReservationButton'>
									<ManualReservation />
								</div>
							</Grid>
						)}
					</Grid>
				</Grid>
				{!hideControls && <Grid item xs={12} className='lastGrid'>
					<Grid container justifyContent='flex-end' gap='24px'>
						<Grid item xs={12}>
							<Grid container>
								<Grid item xs={12} md={6}>
									{switchView === "dayGridMonth" && <SwitchProperties />}
								</Grid>
								<Grid item xs={12} md={6}>
									{isWheelhouseActive && (
										<WheelhouseSection />
									)}
								</Grid>
							</Grid>
						</Grid>

						<Grid item xs={12}>
							<Grid container justifyContent='space-between'>
								<Grid item xs={12} md={6}>
									<ControlsMonthlyView
										calendarTitle={calendarTitle}
										calendarRef={calendarRef}
									/>
								</Grid>
								<Grid item xs={12} md={6}>
									<div className='switchActions'>
										{!isFreemium && switchView === "resourceTimelineWeek" && (
											<CategoryFilter
												selectedCategoryIds={selectedCategoryIds}
												onCategoryChange={onCategoryChange}
												onPropertyIds={onCategoryPropertyIds}
											/>
										)}
										{!isFreemium && <SwitchReservations />}
										<GoToday calendarRef={calendarRef} />
										<SwitchView
											switchView={switchView}
											calendarRef={calendarRef}
											setSwitchView={setSwitchView}
										/>
									</div>
								</Grid>
							</Grid>
						</Grid>
					</Grid>
				</Grid>}
			</Grid>
		</CustomHeaderWrapper>
	)
}

CustomHeader.displayName = 'CustomHeader';

export default memo(CustomHeader);
